import { Injectable, NgZone } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LoaderService } from "./loader.service";
import { RequestService } from "./request.service";
import { LayoutUtilsService } from "./utils/layout-utils.service";
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from "rxjs";
import { ViewInvestigatorDialogComponent } from "src/app/pages/case-page/investigator/view-investigator-dialog/view-investigator-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ViewInvestigatorGroupDialogComponent } from "src/app/pages/case-page/investigator/view-investigator-group-dialog/view-investigator-group-dialog.component";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { SignalingService } from "./signaling.service";
import * as moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { Room, RoomEvent, RemoteParticipant, RemoteTrack } from "livekit-client";
import { SaveSafeRouteCameraRef, SaveSafeRouteDialogComponent, SaveSafeRouteDialogResult } from "src/app/pages/case-page/case-map-page/save-safe-route-dialog/save-safe-route-dialog.component";

export enum TypesEnum {
    VIDEO = 'Video',
    AUDIO = 'Audio',
    IMAGE = 'Image',
    FILE = 'File'
};

export interface OpenSaveRouteDialogOptions {
    selectedCameraIds?: string[];
    selectedColor?: string;
    selectedName?: string;
    selectedDeviationDistanceMeters?: number | null;
    isUpdate?: boolean;
    currentRouteId?: string;
}

export interface SafeRouteCoordinatePayload {
    _id?: string;
    index?: number;
    lng: number;
    lat: number;
    checkpointAt?: string;
    checkpointOffsetMinutes?: number;
    toleranceTimerMinutes?: number;
    isStartPoint?: boolean;
    isEndPoint?: boolean;
}

@Injectable()
export class CaseService {

    public goToLocation: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public setNewCaseSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public caseMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public profile: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public noteMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public userMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public cameraMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public threatMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public userMapMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public investigatorGroupMetadata: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public threatDetected: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public squadMetaList: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public squadGalleryList: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public currentUserGroupsId: any = [];
    public cameras: any = [];
    public buildings: any = [];
    public cameraGroups: any = [];
    public investigatorGroups: any = [];
    public investigators: any = [];
    public squads: any = [];
    public teammembers: any = [];
    public admins: any = [];
    public commanders: any = [];
    public investigatorsAll: any = [];
    public investigatorsGroup: any = [];
    public investigatorsMap: any = [];
    public unresolvedThreats: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    public timeline: any = [];
    public currentUsermap: any = {};
    public tracks: any[] = [];
    public currentUserGroupsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private normalizeCallDomain(domain: string): string {
        return (domain || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');
    }
    public caseLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public caseUpdated: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public investigatorsMapUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public investigatorsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public investigatorsGroupsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public camerasUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public buildingsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public cameraGroupsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public timelineUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public documentsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public markersUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public timelineCollapsed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public dashboardResetView: Subject<void> = new Subject<void>();
    public highlightThreatCamera: BehaviorSubject<string> = new BehaviorSubject<string>('');
    // public squadsUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    // public teammembersUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public reloadThreats: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public reloadCameras: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public camsJoined: any[] = [];
    public cameraSessionInvestigators: string[] = [];
    // Single stream state source for camera UI visibility and media updates.
    public cameraStreamChanged: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public cameraTracksMap: Map<string, any[]> = new Map<string, any[]>();
    // Viewer mute preference per camera; survives stream off/on while the page stays open.
    private cameraAudioMutedMap: Map<string, boolean> = new Map<string, boolean>();
    public noteChangedSubject: Subject<boolean> = new Subject<boolean>();
    // public cameraSteamChanged: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    // public suspeciousLabelDetected: Subject<any> = new Subject<any>();
    // public labelIntervals: { [streamId: string]: any } = {};
    // public threatCapturingStarted: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

    // private isRekognitionWorking: boolean = false;
    // private defaultSettings = {
    //     retrySubscribing: 5000,
    //     audioBlockedTimer: 3000
    // }
    private currentUser: any;
    private tabId: string;
    private channel: BroadcastChannel;
    private cameraRooms: any[] = [];
    private livekitRoomMap: Map<string, any> = new Map<string, any>();
    // private jitsiLibLoaded: boolean = false;
    public antennaUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public routeUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public pinnedCams: any = [];
    // private suspeciousLabels = new Set([
    //     // People & Actions
    //     "gun", "weapon", "knife", "pistol", "rifle", "handgun",
    //     "violence", "fighting", "punch", "blood", "aggression",
    //     "police", "officer", "arrest", "handcuffs",

    //     // dangerous items
    //     "explosive", "fire", "smoke", "molotov", "bullet", "ammunition",
    //     "drugs", "syringe", "needle", "pills", "alcohol", "bottle",

    //     // vehicles & movement
    //     "chase", "crash", "accident",
    //     "speeding",

    //     // situational indicators
    //     "crowd", "protest", "riot", "emergency", "dead body", "injury",
    //     "hostage", "break-in", "trespassing", "theft", "robbery", "suspicious",

    //     // environmental
    //     "abandoned"
    // ]);
    // private threatDetectionVideos: any = new Map<string, HTMLVideoElement>();
    // private rekognitionClient: RekognitionClient;

    constructor(private requestService: RequestService, private zone: NgZone, private layoutUtilsService: LayoutUtilsService, private translate: TranslateService, private loadService: LoaderService, private router: Router, private dialog: MatDialog, private http: HttpClient, private signalingService: SignalingService) {
        this.channel = new BroadcastChannel('case-service');

        // Generate a unique tab ID and store it in sessionStorage
        this.tabId = sessionStorage.getItem('tabId') || this.generateTabId();
        sessionStorage.setItem('tabId', this.tabId);

        // Listen for messages
        this.channel.addEventListener('message', (event) => {
            if (event.data.tabId !== this.tabId) {
                if (this.profile) {
                    let profile = this.profile.getValue();
                    if (profile && profile._id) {
                        if (event.data.caseId === profile._id) {
                            if (event.data.type == 'timeline') {
                                this.timelineUpdated.next(true);
                            } else if (event.data.type == 'case') {
                                this.caseUpdated.next(true);
                            }
                        }
                    }
                }
            }
        });

        this.requestService.currentUserSubject.subscribe((data) => {
            if (data) {
                this.currentUser = data;
            }
        });

        // this.rekognitionClient = new RekognitionClient({
        //     // region: 'us-east-1',
        //     region: environment.environment == 'local' || environment.environment == 'staging' ? 'us-east-1' : 'ap-northeast-1',
        //     credentials: {
        //         accessKeyId: 'AKIAS4IZGWK5WJMBHMXU',
        //         secretAccessKey: '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',
        //         // sessionToken: this.requestService.getToken(), // optional, for Cognito
        //     }
        //     }
        // });
    }
    public generateTabId(): string {
        return Math.random().toString(36).substring(2, 15);
    }

    updateCase(caseId, type = 'all', data = '') {
        if (this.profile) {
            let profile = this.profile.getValue();
            if (profile && profile._id) {
                this.channel.postMessage({ tabId: this.tabId, caseId: caseId, timestamp: Date.now(), type: type, data: data });
            }
        }
    }
    getCaseMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('case').then((metadata: any) => {
                this.caseMetadata.next(metadata.results || []);
                resolve(metadata.results);
            }).catch((e) => reject(e));
        });
    }

    getNoteMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('note').then((metadata: any) => {
                this.noteMetadata.next(metadata.results || []);
                resolve(metadata.results || []);
            }).catch((e) => reject(e));
        });
    }

    getUserMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('user').then((metadata: any) => {
                this.userMetadata.next(metadata.results || []);
                resolve(metadata.results || []);
            }).catch((e) => reject(e));
        });
    }
    getCameraMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('camera').then((metadata: any) => {
                this.cameraMetadata.next(metadata.results || []);
                resolve(metadata.results || []);
            }).catch((e) => reject(e));
        });
    }
    getUserMapMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('caseusermapping').then((metadata: any) => {
                this.userMapMetadata.next(metadata.results || []);
                resolve(metadata.results || []);
            }).catch((e) => reject(e));
        });
    }
    getInvestigatorGroupMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('investigatorgroup').then((metadata: any) => {
                this.investigatorGroupMetadata.next(metadata.results || []);
                resolve(metadata.results || []);
            }).catch((e) => reject(e));
        });
    }

    getCase(caseId: string, skipSubs = false): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getSingleData('case', caseId, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    if (!skipSubs) {
                        this.profile.next(data.results);
                    }
                    resolve(data.results);
                }
            });
        });
    }
    getNote(noteId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getSingleData('note', noteId, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                }
            });
        });
    }
    private getMetadataPromise(type: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getMetaData(type, undefined, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data);
                }
            });
        });
    }

    getDocuments(pageIndex = 0, pageSize = 10, caseId, searchValue, type, selectedIds = undefined, startDate = undefined, endDate = undefined, group = undefined): Promise<void> {
        return new Promise((resolve, reject) => {
            let andFilter: any = [
                { "organizationId._id": { "$in": [this.requestService.orgId] } },
            ];
            if (caseId) {
                andFilter.push({ "case._id": { "$eq": caseId } });
            }
            if (group) {
                andFilter.push({ "group": { "$eq": group } });
            }

            if (type) {
                if (Array.isArray(type)) {
                    andFilter.push({ "type": { "$in": type } });
                } else {
                    andFilter.push({ "type": { "$eq": type } });
                }
            }
            if (selectedIds) {
                andFilter.push({ "_id": { "$in": selectedIds } });
            }

            // Add date range filter if provided
            if (startDate || endDate) {
                let dateFilter: any = {};
                if (startDate) {
                    const formattedStartDate = moment(startDate).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
                    dateFilter["$gte"] = formattedStartDate;
                }
                if (endDate) {
                    const formattedEndDate = moment(endDate).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
                    dateFilter["$lte"] = formattedEndDate;
                }
                if (Object.keys(dateFilter).length > 0) {
                    andFilter.push({ "createdAt": dateFilter });
                }
            }

            this.requestService.postRequest('doc', 'search', {
                page: pageIndex + 1,
                count: pageSize,
                order: [{ "field": "createdAt", "order": "desc" }],
                fields: ["createdAt", "name", "case", "type", "url", "createdName", "ai_flag", "description"],
                termfieldstermfields: ['name'],
                term: searchValue,
                filter: {
                    "$and": andFilter
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data);
                }
            });
        });
    }

    getInvestigatorGroups(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/investigatorgroup/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    data.results = data.results.map((group, index) => ({
                        ...group,
                        investigatorNames: group.investigators.map(inv => (inv.alternative_name || inv.name)).join(', '),
                        investigatorIds: group.investigators.map(inv => (inv._id)),
                        shortId: (index + 1) < 10 ? '0' + (index + 1) : (index + 1)
                    }));

                    // this.investigatorgroups.map((i, index) => {
                    //     i.shortId = (index + 1) < 10 ? '0' + (index + 1) : (index + 1);
                    // });
                    data.results.map(i => {
                        i.isGroup = true;
                    });
                    this.investigatorGroups = data.results;
                    resolve(data.results);
                }
            });
        });
    }

    getInvestigatorsList(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/investigators/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    // this.investigators = data.results;
                    resolve(data);
                }
            });
        });
    }
    public getAdminsPromise(casePrefecture: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // this.requestService.postRequest('user', 'search', {
            //     order: [{ "field": "alternative_name", "order": "asc" }],
            //     fields: ["resources", "name", "alternative_name", "deviceids", "pictureLink"],
            //     term: '',
            //     filter: {
            //         "$and": [{ "organizationId._id": { "$in": [this.requestService.orgId] } }, {
            //             "resources._id": { "$in": [environment.customKeys.roleAdmin] },
            //             "prefecture": { "$eq": casePrefecture }
            //         }]
            //     }
            // }, (data, error) => {
            //     if (data?.results?.length) {
            //         // this.displayAdmins = data.results;
            //         data.results.map(i => {
            //             i.role = 'Admin';
            //         });
            //         this.admins = data.results;
            //         resolve(data.results);
            //     }
            //     else {
            //         resolve();
            //     }
            // });
            this.admins = [];
            resolve();
        });
    }

    public getCommandersPromise(casePrefecture: string): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('user', 'search', {
                order: [{ "field": "alternative_name", "order": "asc" }],
                fields: ["resources", "name", "alternative_name", "deviceids", "pictureLink", "isAdmin", "isSuperAdmin", "isInCall", "isLiveStreaming"],
                term: '',
                filter: {
                    "$and": [{ "organizationId._id": { "$in": [this.requestService.orgId] } }, { "resources._id": { "$in": [environment.customKeys.rolePrefectureCommanders] } }, { "prefecture": { "$eq": casePrefecture } }]
                }
            }, (data, error) => {
                if (data?.results?.length) {
                    // this.displayCommanders = data.results;
                    data.results.map(i => {
                        i.role = 'Commander';
                    });
                    this.commanders = data.results;
                    resolve(data.results);
                }
                else {
                    resolve();
                }
            });
        });
    }
    getInvestigators(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/user/${caseId}/all`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.investigators = data.results;
                    this.investigatorsAll = data.results;
                    resolve(data);
                }
            });
        });
    }

    public triggerCamerasCaseUpdate(caseId: string): Promise<void> {
        const camerasPromise = this.getCameras(caseId);
        const cameraGroupsPromise = this.getCameraGroups(caseId);

        return Promise.all([camerasPromise, cameraGroupsPromise])
            .then(() => {
                this.camerasUpdated.next(true);
                // this.cameraGroupsUpdated.next(true);
            })
            .catch(() => {
                // swallow errors to avoid breaking caller flows
            })
            .then(() => { });
    }
    public triggerInvestigatorsCaseUpdate(caseId: string): Promise<void> {
        const investigatorsPromise = this.getInvestigators(caseId);
        const investigatorGroupsPromise = this.getInvestigatorGroups(caseId);

        return Promise.all([investigatorsPromise, investigatorGroupsPromise])
            .then(() => {
                this.investigatorsUpdated.next(true);
                this.investigatorsGroupsUpdated.next(true);
            })
            .catch(() => {
                // swallow errors to avoid breaking caller flows
            })
            .then(() => { });
    }
    getInvestigatorsGroup(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/investigatorgroup/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    data.results.map(i => {
                        i.isGroup = true;
                    });
                    this.investigatorsGroup = data.results;
                    resolve(data);
                }
            });
        });
    }
    getCurrentInvestigatorGroups(userId, skipUpdate = true): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `investigatorgroup/user/${userId}/get`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    if (skipUpdate)
                        this.currentUserGroupsId = data.results.map(itm => itm._id);
                    resolve(data);
                }
            });
        });
    }
    getInvestigatorsCaseData(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/caseusermapping/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.investigatorsMap = data.results;

                    resolve(data);
                }
            });
        });
    }
    getInvestigatorCaseData(caseId, userId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/caseusermapping/${caseId}/list/${userId}`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data);
                }
            });
        });
    }
    getCameras(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/camera/${caseId}/all`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.cameras = data.results;
                    resolve(data);
                }
            });
        });
    }

    getCameraGroups(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/cameragroup/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.cameraGroups = data.results;
                    resolve(data);
                }
            });
        });
    }
    getMarkers(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `markers/case/${caseId}`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.squads = data.results.filter(itm => itm.type == 'squad');
                    this.teammembers = data.results.filter(itm => itm.type == 'teammember');
                    resolve(data?.results || []);
                }
            });
        });
    }
    secureCameras() {
        let currentUserMap = this.currentUsermap;
        if (!currentUserMap || !currentUserMap.timeline_visibility || currentUserMap.timeline_visibility === '' || currentUserMap.timeline_visibility === 'filtered') {
            let hiddenCams = this.cameras.filter((itm) => itm.invisible).map(itm => itm._id);
            this.cameras = this.cameras.filter((itm) => !itm.invisible);

            this.cameraGroups = this.cameraGroups.map((itm) => {
                let cameras = itm.cameras.filter(itm => !hiddenCams.includes(itm._id))
                itm['cameras'] = cameras;
                return itm;
            })

        }
    }
    getTimelinePromise(caseId: string, pageIndex: number, pageSize: number, filters?: any): Promise<void> {
        return new Promise((resolve, reject) => {
            let andFilter: any = [
                { "organizationId._id": { "$in": [this.requestService.orgId] } }
            ];
            if (caseId) {
                andFilter.push({ "case._id": { "$eq": caseId } });
            }
            let documentFilter = {};

            if (filters) {
                let investigators = [];
                if (filters.investigators?.length && !filters.investigators.includes("L1")) {
                    investigators = filters.investigators;
                }
                if (filters.fileType?.length) {
                    documentFilter = {
                        "document.type": { "$in": filters.fileType }
                    }
                }
                if (filters.visibility?.length) {
                    andFilter.push({
                        "visibility": { "$in": filters.visibility }
                    })
                }
                if (filters.filtervisibility) {
                    if (filters.filtervisibility.onlyGroup) {
                        andFilter.push(
                            { "investigatorgroup._id": { "$in": filters.filtervisibility.userGroupsIds } }
                        );
                    } else {
                        let orObject: any[] = [];
                        investigators.push(filters.filtervisibility.userId);
                        orObject = [
                            { "investigators._id": { "$in": investigators } },
                            { "initiatedFrom._id": { "$in": investigators } },
                            { "investigatorgroup._id": { "$in": filters.filtervisibility.userGroupsIds } },
                            { "createdBy": { "$eq": filters.filtervisibility.userId } }
                        ]
                        if (!filters.filtervisibility.excludeVisibility) {
                            orObject.push({ "visibility": { "$in": ["green"] } })
                        }
                        andFilter.push({
                            "$or": orObject
                        });
                    }
                    // andFilter.push({ "visibility": { "$in": ["green"] } });
                } else {
                    if (investigators.length > 0) {
                        andFilter.push({
                            "$or": [
                                { "investigators._id": { "$in": investigators } },
                                { "initiatedFrom._id": { "$in": investigators } }
                            ]
                        });
                    }
                }
                if (filters.hasOwnProperty('type') && filters.type) {
                    const applyInterestFilters = (types: string[]) => {
                        const hasNote = types.includes('note');
                        const hasCarOfInterest = types.includes('carOfInterest');
                        const hasPersonOfInterest = types.includes('personOfInterest');

                        if (hasCarOfInterest) {
                            andFilter.push({
                                "car_of_interest": { "$eq": true }
                            });
                        } else if (hasNote) {
                            andFilter.push({
                                "car_of_interest": { "$eq": false }
                            });
                        }

                        if (hasPersonOfInterest) {
                            andFilter.push({
                                "person_of_interest": { "$eq": true }
                            });
                        } else if (hasNote) {
                            andFilter.push({
                                "person_of_interest": { "$eq": false }
                            });
                        }
                    };

                    const mapTypeForDb = (type: string) => {
                        if (type === 'carOfInterest' || type === 'personOfInterest') {
                            return 'note';
                        }
                        return type;
                    };

                    if (Array.isArray(filters.type)) {
                        const types = filters.type;
                        if (types.length === 0) {
                            andFilter.push({
                                "type": { "$ne": 'call' }
                            })
                        } else {
                            const mappedTypes = Array.from(new Set(types.map(mapTypeForDb)));
                            let typeFilter = { "$in": mappedTypes };
                            andFilter.push({
                                "type": typeFilter
                            });
                            applyInterestFilters(types);
                        }
                    } else {
                        const type = filters.type;
                        const dbType = mapTypeForDb(type);
                        let typeFilter = { "$eq": dbType };
                        andFilter.push({
                            "type": typeFilter
                        });
                        applyInterestFilters([type]);
                    }
                } else {
                    andFilter.push({
                        "type": { "$ne": 'call' }
                    })
                }
            } else {
                andFilter.push({
                    "type": { "$ne": 'call' }
                })
            }

            if (filters?.mapMarkersOnly) {
                andFilter.push({
                    "$and": [
                        { "geolocation": { "$exists": true } },
                        { "geolocation": { "$ne": "" } },
                        { "geolocation": { "$ne": null } }
                    ]
                });
            }

            this.requestService.postRequest('note', 'search', {
                page: pageIndex + 1,
                count: pageSize,
                "order": [{ "field": "updatedAt", "order": "desc" }], "fields": ["createdAt", "createdBy", "name", "camera", "case_status", "case", "casetype", "type", "notes", "createdName", "location", "updatedAt", "geolocation", "action_date", "plate_of_interest", "person_of_interest_name", "person_of_interest_description", "car_of_interest", "person_of_interest", "investigators", "investigatorgroup", "car_of_interest_description", "car_of_interest_identifier", "car_color", "initiatedFrom", "car_model", "car_make", "car_type", "person_of_interest_gender", "visibility", "cameras", "documents", "threat_type", "threat_types", "threat", "person_of_interest_dob", "person_of_interest_approximate_age", "person_of_interest_hair_color", "person_of_interest_height", "person_of_interest_weight", "person_of_interest_eye_color", "person_of_interest_race", "person_of_interest_description", "person_of_interest_distinguishing_features", "person_of_interest_gender_other", "excludeFromMap", "alertSentTo"], "term": "",
                "filter": {
                    "$and": andFilter
                },
                document: documentFilter,
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data);
                }
            });
        });
    }

    getUserList(users: any, groups: any) {
        let userNames = [];
        users?.forEach(user => {
            userNames.push(user.alternative_name || user.name);
        });
        groups?.forEach(group => {
            userNames.push(group.name);
        });
        let names = userNames.join(", ");
        if (userNames.length > 1) {
            names = names.replace(new RegExp(',' + "(?=[^" + ',' + "]*$)"), ' and ');
        }
        return names;
    }

    viewProfile(userId: string, userMetadata: any, caseId: string = undefined, userMapMetadata: any = undefined, userMapData: any = undefined, hideCall: boolean = false, squadMarker: boolean = false) {
        this.loadService.display(true);
        this.requestService.getRecord(userId, 'user', (data, error) => {
            if (data) {
                this.loadService.display(false);
                const dialog = this.dialog.open(ViewInvestigatorDialogComponent, {
                    autoFocus: false,
                    data: {
                        name: data.results.alternative_name,
                        badgeNo: data.results.badge_no,
                        rank: data.results.rank,
                        division: data.results.division,
                        resources: data.results.resources,
                        pictureLink: data.results.pictureLink,
                        deviceids: data.results.deviceids,
                        phone: data.results.phone,
                        clothing: data.results.clothing,
                        alternative_name: data.results.alternative_name,
                        caseId: caseId,
                        userMetadata: userMetadata,
                        userMapMetadata: userMapMetadata,
                        squad_ref: userMapData?.squad_ref,
                        remarks: userMapData?.remarks,
                        call_sign: data.results.call_sign,
                        _id: data.results._id,
                        role: data.results.resources[0],
                        hideCall: hideCall,
                        squadMarker: squadMarker,
                        region: data.results.region,
                        prefecture: data.results.prefecture
                    },
                    disableClose: false,
                    width: 'fit-content',
                })
            }
            if (error) {
                this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
                this.loadService.display(false);
            }
        });
    }
    mapUsersData(investigators, investigatorsMapData) {
        let investigatorsMapped = investigators.map((user) => {
            return this.mapUserData(user, investigatorsMapData);
        });
        return investigatorsMapped;
    }
    mapUserData(investigator, investigatorsMapData) {
        investigator['role'] = 'Investigator';
        let userMapData = investigatorsMapData.find(itm => investigator._id === itm.user._id);
        if (userMapData) {
            investigator['squad_ref'] = userMapData['squad_ref'];
            investigator['remarks'] = userMapData['remarks'];
            investigator['isPinned'] = userMapData['isPinned'];
            // investigator['call_sign'] = userMapData['call_sign'];
            investigator['timeline_visibility'] = userMapData['timeline_visibility'];
        }
        return investigator;
    }
    viewInvestigatorGroupProfile(groupId: string, groupMetadata: any, caseId: string = undefined, userMetadata: any = undefined, userMapMetadata: any = undefined, investigatorsMapData: any[] = undefined, hideCall: boolean = false, squadMarker: boolean = false) {
        this.loadService.display(true);
        this.requestService.getRecord(groupId, 'investigatorgroup', (data, error) => {
            if (data) {
                // console.log(data);
                this.loadService.display(false);
                const dialog = this.dialog.open(ViewInvestigatorGroupDialogComponent, {
                    autoFocus: false,
                    data: {
                        name: data.results.name,
                        pictureLink: data.results.picture,
                        investigators: data.results.investigators,
                        investigatorsMapData: investigatorsMapData,
                        groupMetadata: groupMetadata,
                        userMetadata: userMetadata,
                        userMapMetadata: userMapMetadata,
                        caseId: caseId,
                        _id: data.results._id,
                        hideCall: hideCall,
                        squadMarker: squadMarker
                    },
                    disableClose: false,
                    width: 'fit-content',
                })
            }
            if (error) {
                this.loadService.display(false);
                this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
            }
        });
    }

    public getPeople(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('list', 'note/persons', (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    getAssignedCasesPromise(userId: string, userGroupsIds: string[] = []): Promise<void> {
        return new Promise((resolve, reject) => {
            const orConditions: any[] = [
                { "investigators._id": { "$in": [userId] } }
            ];

            if (userGroupsIds && userGroupsIds.length > 0) {
                orConditions.push({ "investigatorgroup._id": { "$in": userGroupsIds } });
            }

            let andFilter: any = [
                { "organizationId._id": { "$in": [this.requestService.orgId] } },
                {
                    '$or': orConditions
                }
            ];

            this.requestService.postRequest('case', 'search', {
                order: [{ "field": "name", "order": "asc" }],
                fields: ["case_id", "createdAt"],
                term: '',
                filter: {
                    "$and": andFilter
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                }
            });
        });
    }
    getActiveCasesPromise(selectedRegion: string = undefined, selectedPrefecture: string = undefined, isSuperAdmin: boolean = false): Promise<void> {
        return new Promise((resolve, reject) => {
            let andFilter: any = [{ "organizationId._id": { "$in": [this.requestService.orgId] } }];
            andFilter.push({ "status": { "$eq": 'active' } });
            if (!isSuperAdmin) {
                if (selectedRegion) {
                    andFilter.push({ "region": { "$eq": selectedRegion } });
                }
                if (selectedPrefecture) {
                    andFilter.push({ "prefecture": { "$eq": selectedPrefecture } });
                }
            }
            this.requestService.postRequest('case', 'search', {
                order: [{ "field": "createdAt", "order": "desc" }],
                fields: ["name", "case_id", "createdAt"],
                term: '',
                page: 1,
                // count: 50,
                filter: {
                    "$and": andFilter
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                }
            });
        });
    }

    private joinCameraSession(caseId: string, investigatorId: string, currentUser, streamType: string): Promise<any> {
        return new Promise((resolve, reject) => {
            // this.loadService.display(true);
            this.requestService.postRequest('call', 'camera/createsession', {
                userId: currentUser._id,
                hostId: caseId,
                caseId: caseId,
                type: streamType
            }, (sessionData, error) => {
                if (sessionData?.results?.callSessionId) {
                    const callSessionId = sessionData.results.callSessionId;
                    const identityId = `${currentUser._id}${Math.random().toString(36).slice(2, 7)}`;
                    this.requestService.postRequest('call', 'camera/createtoken/v1', {
                        userId: currentUser._id,
                        name: currentUser.alternative_name,
                        investigatorId: investigatorId,
                        identity: identityId,
                        userImage: currentUser.pictureLink || `${this.requestService.serverHostUrl}/assets/images/profile.png`,
                        hostId: caseId,
                        caseId: caseId,
                        callSessionId: callSessionId
                    }, (data, error) => {
                        if (data) {
                            const token = data?.results?.token || data?.results;
                            this.loadService.display(false);
                            resolve({ callSessionId, token, currentUser });
                        }
                        if (error) {
                            this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
                            this.loadService.display(false);
                            reject(error);
                        }
                    });
                } else if (error) {
                    this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
                    this.loadService.display(false);
                    reject(error);
                }
            });
        });
    }

    public async startCameraSession(caseId: string, selectedUser: any = this.requestService.currentUserSubject.getValue(), streamType: string = 'camera') {
        if (!caseId || !selectedUser || !selectedUser._id) {
            return;
        }

        const investigatorId = selectedUser._id;
        const existingRoom = this.livekitRoomMap.get(investigatorId);
        if (existingRoom) {
            this.trackCameraSessionMetadata(investigatorId, caseId, streamType);
            return;
        }

        try {
            const { callSessionId, token } = await this.joinCameraSession(caseId, investigatorId, selectedUser, streamType);
            await this.initLivekitCameraSession(callSessionId, token, investigatorId);
            this.trackCameraSessionMetadata(investigatorId, caseId, streamType);
        } catch (error) {
            console.error('LiveKit camera session join failed', error);
        }
    }

    private trackCameraSessionMetadata(investigatorId: string, caseId: string, streamType: string): void {
        const roomState = this.livekitRoomMap.get(investigatorId);
        if (!roomState) {
            return;
        }
        if (!roomState.streamTypes) {
            roomState.streamTypes = new Set<string>();
        }
        roomState.streamTypes.add(streamType);
        if (!roomState.caseId) {
            roomState.caseId = caseId;
        }
    }

    public stopCameraSession(caseId: string, selectedUser: any = this.requestService.currentUserSubject.getValue(), streamType: string = 'camera') {
        if (!caseId || !selectedUser || !selectedUser._id) {
            return;
        }

        const investigatorId = selectedUser._id;
        const roomState = this.livekitRoomMap.get(investigatorId);
        if (!roomState || roomState.caseId !== caseId) {
            return;
        }

        if (!roomState.streamTypes) {
            this.disconnectCameraSession(roomState.room, undefined, investigatorId);
            return;
        }

        roomState.streamTypes.delete(streamType);
        if (roomState.streamTypes.size === 0) {
            this.disconnectCameraSession(roomState.room, undefined, investigatorId);
        }
    }

    // public async joinInvestigatorCameraSession(caseId: string, investigatorId: string, selectedUser: any = this.requestService.currentUserSubject.getValue()) {
    //     if (!caseId || !investigatorId || !selectedUser || !selectedUser._id) {
    //         return;
    //     }
    //     try {
    //         const { callSessionId, token } = await this.joinCameraSession(caseId, investigatorId, selectedUser);
    //         await this.initLivekitCameraSession(callSessionId, token, investigatorId);
    //     } catch (error) {
    //         console.error('LiveKit investigator camera join failed', error, investigatorId);
    //     }
    // }

    // public updateCameraSessions(investigatorIds: string[], caseId: string, selectedUser: any) {
    //     if (!investigatorIds?.length) return;
    //     const idsToStart = investigatorIds.filter(id => !this.cameraSessionInvestigators.includes(id));
    //     if (!idsToStart.length) return;

    //     // idsToStart.forEach(id => {
    //     //     this.cameraSessionInvestigators.push(id);
    //     //     this.joinInvestigatorCameraSession(caseId, id, selectedUser);
    //     // });
    // }

    private async initLivekitCameraSession(callSessionId: string, token: string, investigatorId: string): Promise<void> {
        if (!callSessionId || !token || !investigatorId) return;
        if (this.livekitRoomMap.has(investigatorId)) return;

        const livekitUrl = this.resolveLivekitUrl();
        if (!livekitUrl) {
            this.layoutUtilsService.showNotification(this.translate.instant('Error: Missing LiveKit URL.'), this.translate.instant('Dismiss'));
            return;
        }

        const room = new Room({
            adaptiveStream: true,
            dynacast: true,
        });
        const roomState = {
            investigatorId,
            callSessionId,
            room,
            trackWrappers: new Map<string, any>(),
            participantTracks: new Map<string, Set<string>>()
        };

        room
            .on(RoomEvent.TrackSubscribed, (track: RemoteTrack, publication: any, participant: RemoteParticipant) => {
                this.onLivekitTrackSubscribed(roomState, track, publication, participant);
            })
            .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, publication: any, participant: RemoteParticipant) => {
                this.onLivekitTrackUnsubscribed(roomState, track, publication, participant);
            })
            .on(RoomEvent.TrackMuted, (publication: any, participant: any) => {
                this.onLivekitTrackMuted(roomState, publication, participant);
            })
            .on(RoomEvent.TrackUnmuted, (publication: any, participant: any) => {
                this.onLivekitTrackUnmuted(roomState, publication, participant);
            })
            .on(RoomEvent.TrackUnpublished, (publication: any, participant: RemoteParticipant) => {
                this.onLivekitTrackUnpublished(roomState, publication, participant);
            })
            .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
                this.onLivekitParticipantDisconnected(roomState, participant);
            })
            .on(RoomEvent.Disconnected, () => {
                if (this.livekitRoomMap.has(roomState.investigatorId)) {
                    this.disconnectCameraSession(roomState.room, undefined, roomState.investigatorId);
                }
            });

        try {
            await room.connect(livekitUrl, token, { autoSubscribe: true });
            this.livekitRoomMap.set(investigatorId, roomState);
            this.cameraRooms.push(roomState);
            try {
                await room.startAudio();
                // console.log('[LiveKit camera] room.startAudio resolved', {
                //     investigatorId: roomState.investigatorId,
                //     canPlaybackAudio: (room as any).canPlaybackAudio,
                //     source: 'initLivekitCameraSession'
                // });
            } catch (error: any) {
                console.error('[LiveKit camera] room.startAudio failed', {
                    investigatorId: roomState.investigatorId,
                    source: 'initLivekitCameraSession',
                    name: error?.name,
                    message: error?.message
                });
            }
        } catch (e) {
            room.disconnect();
            throw e;
        }
    }

    private onLivekitTrackSubscribed(roomState: any, track: RemoteTrack, publication: any, participant: RemoteParticipant): void {
        if (!track) return;
        const ownerId = this.resolveTrackOwnerId(roomState, participant);
        const trackSid = publication?.trackSid || track.sid || `${participant?.sid || participant?.identity || 'participant'}-${track.kind}`;
        const wrappedTrack = this.toLegacyTrackAdapter(track, participant);

        roomState.trackWrappers.set(trackSid, wrappedTrack);
        const participantKey = participant?.sid || participant?.identity || '';
        if (participantKey) {
            if (!roomState.participantTracks.has(participantKey)) {
                roomState.participantTracks.set(participantKey, new Set<string>());
            }
            roomState.participantTracks.get(participantKey).add(trackSid);
        }

        const tracks = this.cameraTracksMap.get(ownerId) || [];
        const existing = tracks.find((t: any) => t.getId && t.getId() === wrappedTrack.getId());
        if (!existing) {
            tracks.push(wrappedTrack);
            this.cameraTracksMap.set(ownerId, tracks);
        }

        const visible = this.syncInvestigatorStreamingState(ownerId);

        const payload = {
            investigatorId: ownerId,
            participantId: participant?.identity || participant?.sid,
            track: wrappedTrack,
            kind: wrappedTrack.getType(),
            state: 'started',
            visible
        };
        this.cameraStreamChanged.next(payload);

        // const audioInfo = this.getParticipantAudioInfo(participant as any);
        // console.log('[LiveKit camera] track subscribed', {
        //     investigatorId: ownerId,
        //     participantId: participant?.identity || participant?.sid,
        //     subscribedKind: track.kind,
        //     hasAudioTrack: audioInfo.hasAudioTrack,
        //     audioMuted: audioInfo.audioMuted
        // });
    }

    private onLivekitTrackUnsubscribed(roomState: any, track: RemoteTrack, publication: any, participant: RemoteParticipant): void {
        if (!track) return;
        const ownerId = this.resolveTrackOwnerId(roomState, participant);
        const trackSid = publication?.trackSid || track.sid || `${participant?.sid || participant?.identity || 'participant'}-${track.kind}`;
        const wrappedTrack = roomState.trackWrappers.get(trackSid);
        if (wrappedTrack) {
            roomState.trackWrappers.delete(trackSid);
        }

        const participantKey = participant?.sid || participant?.identity || '';
        if (participantKey && roomState.participantTracks.has(participantKey)) {
            const ids = roomState.participantTracks.get(participantKey);
            ids.delete(trackSid);
            if (ids.size === 0) {
                roomState.participantTracks.delete(participantKey);
            }
        }

        const tracks = this.cameraTracksMap.get(ownerId) || [];
        const filteredTracks = tracks.filter((t: any) => {
            if (wrappedTrack && t === wrappedTrack) return false;
            return !(t?.getId && t.getId() === trackSid);
        });
        this.cameraTracksMap.set(ownerId, filteredTracks);
        const visible = this.syncInvestigatorStreamingState(ownerId);

        if (wrappedTrack) {
            const payload = {
                investigatorId: ownerId,
                participantId: participant?.identity || participant?.sid,
                track: wrappedTrack,
                kind: wrappedTrack.getType(),
                state: 'destroyed',
                visible
            };
            this.cameraStreamChanged.next(payload);
        }
    }

    private onLivekitTrackMuted(roomState: any, publication: any, participant: any): void {
        if (!publication || publication.kind !== 'video') return;
        // const trackSid = publication.trackSid;
        // const wrappedTrack = trackSid ? roomState.trackWrappers.get(trackSid) : undefined;
        // const ownerId = this.resolveTrackOwnerId(roomState, participant, wrappedTrack);
        // const visible = this.syncInvestigatorStreamingState(ownerId);

        // console.log('[LiveKit camera] track muted', {
        //     investigatorId: ownerId,
        //     participantId: participant?.identity || participant?.sid,
        //     kind: publication.kind,
        //     trackSid,
        //     visible
        // });
    }

    private onLivekitTrackUnmuted(roomState: any, publication: any, participant: any): void {
        if (!publication || publication.kind !== 'video') return;
        const track = publication.track as RemoteTrack;
        if (track) {
            this.onLivekitTrackSubscribed(roomState, track, publication, participant);
            return;
        }

        const trackSid = publication.trackSid;
        if (!trackSid) return;
        const wrappedTrack = roomState.trackWrappers.get(trackSid);
        if (!wrappedTrack) return;
        const ownerId = this.resolveTrackOwnerId(roomState, participant, wrappedTrack);
        const visible = this.syncInvestigatorStreamingState(ownerId);

        this.cameraStreamChanged.next({
            investigatorId: ownerId,
            participantId: participant?.identity || participant?.sid,
            track: wrappedTrack,
            kind: wrappedTrack.getType ? wrappedTrack.getType() : 'video',
            state: 'started',
            visible
        });
    }

    private onLivekitTrackUnpublished(roomState: any, publication: any, participant: RemoteParticipant): void {
        if (!publication) return;
        const trackSid = publication.trackSid;
        if (!trackSid) return;
        const wrappedTrack = roomState.trackWrappers.get(trackSid);
        if (!wrappedTrack) return;
        const ownerId = this.resolveTrackOwnerId(roomState, participant, wrappedTrack);

        roomState.trackWrappers.delete(trackSid);
        const tracks = this.cameraTracksMap.get(ownerId) || [];
        const filteredTracks = tracks.filter((t: any) => !(t?.getId && t.getId() === trackSid));
        this.cameraTracksMap.set(ownerId, filteredTracks);
        const visible = this.syncInvestigatorStreamingState(ownerId);

        this.cameraStreamChanged.next({
            investigatorId: ownerId,
            participantId: participant?.identity || participant?.sid,
            track: wrappedTrack,
            kind: wrappedTrack.getType ? wrappedTrack.getType() : publication.kind,
            state: 'destroyed',
            visible
        });
    }

    private onLivekitParticipantDisconnected(roomState: any, participant: RemoteParticipant): void {
        const participantKey = participant?.sid || participant?.identity || '';
        if (!participantKey) return;
        const trackIds: Set<string> = roomState.participantTracks.get(participantKey) || new Set<string>();
        trackIds.forEach((trackSid) => {
            const wrappedTrack = roomState.trackWrappers.get(trackSid);
            const ownerId = this.resolveTrackOwnerId(roomState, participant, wrappedTrack);
            if (wrappedTrack) {
                const payload = {
                    investigatorId: ownerId,
                    participantId: participant?.identity || participant?.sid,
                    track: wrappedTrack,
                    kind: wrappedTrack.getType(),
                    state: 'destroyed',
                    visible: false
                };
                this.cameraStreamChanged.next(payload);

                const ownerTracks = this.cameraTracksMap.get(ownerId) || [];
                const nextTracks = ownerTracks.filter((t: any) => {
                    if (t === wrappedTrack) return false;
                    return !(t?.getId && t.getId() === trackSid);
                });
                this.cameraTracksMap.set(ownerId, nextTracks);
                this.syncInvestigatorStreamingState(ownerId);
            }
            roomState.trackWrappers.delete(trackSid);
        });
        roomState.participantTracks.delete(participantKey);
    }

    private resolveTrackOwnerId(roomState: any, participant?: any, wrappedTrack?: any): string {
        const participantIdentity = participant?.identity || '';
        if (participantIdentity) return participantIdentity;

        const wrappedParticipantId = wrappedTrack?.participantId || '';
        if (wrappedParticipantId) return wrappedParticipantId;

        return roomState?.investigatorId || '';
    }

    private syncInvestigatorStreamingState(investigatorId: string): boolean {
        const tracks = this.cameraTracksMap.get(investigatorId) || [];
        const hasMediaTrack = tracks.some((track: any) => {
            const type = track?.getType && track.getType();
            return type === 'video' || type === 'audio';
        });
        if (hasMediaTrack) {
            if (!this.camsJoined.includes(investigatorId)) {
                this.camsJoined.push(investigatorId);
            }
        } else {
            this.camsJoined = this.camsJoined.filter((id) => id !== investigatorId);
        }
        // console.log('[LiveKit camera] sync streaming state', this.camsJoined);
        return hasMediaTrack;
    }

    private getParticipantAudioInfo(participant: any): { hasAudioTrack: boolean, audioMuted: boolean | null } {
        if (!participant?.audioTrackPublications?.forEach) {
            return { hasAudioTrack: false, audioMuted: null };
        }

        let hasAudioTrack = false;
        let audioMuted: boolean | null = null;
        participant.audioTrackPublications.forEach((publication: any) => {
            const isSubscribed = !!publication?.track;
            if (isSubscribed) {
                hasAudioTrack = true;
                if (audioMuted === null) {
                    audioMuted = !!publication?.isMuted;
                }
            }
        });

        return { hasAudioTrack, audioMuted };
    }

    private toLegacyTrackAdapter(track: RemoteTrack, participant: RemoteParticipant): any {
        const participantId = participant?.identity || participant?.sid;
        return {
            sid: track.sid,
            kind: track.kind,
            participantId: participantId,
            attach: (element: HTMLMediaElement) => track.attach(element),
            detach: (element: HTMLMediaElement) => track.detach(element),
            isLocal: () => false,
            getType: () => track.kind,
            getId: () => track.sid,
            getParticipantId: () => participantId,
            isMuted: () => !!track.isMuted,
        };
    }

    private resolveLivekitUrl(): string {
        const fromEnv = (environment as any).livekitUrl;
        if (fromEnv) return fromEnv;

        const rawDomain = (environment as any).callDomain;
        if (!rawDomain) return '';
        if (rawDomain.startsWith('wss://') || rawDomain.startsWith('ws://')) {
            return rawDomain;
        }
        const normalized = rawDomain.replace(/^https?:\/\//, '');
        return `wss://${normalized}`;
    }

    private disconnectCameraSession(cameraRoom: any, cameraConnection: any, investigatorId: string): void {
        try {
            if (cameraRoom && typeof cameraRoom.disconnect === 'function') {
                cameraRoom.disconnect();
                cameraRoom = null;
            } else if (cameraRoom) {
                if (typeof cameraRoom.leave === 'function') {
                    cameraRoom.leave();
                }
                cameraRoom.tracks = [];
                cameraRoom = null;
            }
            if (cameraConnection) {
                cameraConnection.disconnect();
                cameraConnection = null;
            }

            this.livekitRoomMap.delete(investigatorId);
            this.cameraRooms = this.cameraRooms.filter((roomRef: any) => roomRef?.investigatorId !== investigatorId);
            this.cameraTracksMap.delete(investigatorId);
            this.camsJoined = this.camsJoined.filter((id) => id !== investigatorId);
            this.cameraStreamChanged.next({ investigatorId, track: undefined, state: 'destroyed', visible: false });
        } catch (error) {
            console.error('Camera session disconnect failed', error);
        }
    }

    public disconnectAllCameras() {
        // Prefer livekitRoomMap so orphaned rooms (not mirrored in cameraRooms) are also torn down.
        const roomsByInvestigator = new Map<string, any>();
        this.livekitRoomMap.forEach((roomState, investigatorId) => {
            if (investigatorId) {
                roomsByInvestigator.set(investigatorId, roomState);
            }
        });
        this.cameraRooms.forEach((cameraRoom) => {
            const investigatorId = cameraRoom?.investigatorId;
            if (investigatorId && !roomsByInvestigator.has(investigatorId)) {
                roomsByInvestigator.set(investigatorId, cameraRoom);
            }
        });

        roomsByInvestigator.forEach((cameraRoom, investigatorId) => {
            this.disconnectCameraSession(
                cameraRoom?.room ?? cameraRoom,
                cameraRoom?.connection,
                investigatorId
            );
        });

        this.cameraRooms = [];
        this.livekitRoomMap.clear();
        this.cameraTracksMap.clear();
        this.cameraAudioMutedMap.clear();
        this.camsJoined = [];
        this.cameraSessionInvestigators = [];
        this.cameraStreamChanged.next(undefined);
    }

    public async startCameraAudioPlayback(investigatorId?: string): Promise<void> {
        const targetRooms = investigatorId
            ? this.cameraRooms.filter((cameraRoom) => cameraRoom?.investigatorId === investigatorId)
            : this.cameraRooms;
        for (const cameraRoom of targetRooms) {
            const room = cameraRoom?.room;
            if (!room || typeof room.startAudio !== 'function') {
                continue;
            }
            try {
                await room.startAudio();
                // console.log('[LiveKit camera] room.startAudio resolved', {
                //     investigatorId: cameraRoom?.investigatorId,
                //     canPlaybackAudio: (room as any).canPlaybackAudio
                // });
            } catch (error: any) {
                console.error('[LiveKit camera] room.startAudio failed', {
                    investigatorId: cameraRoom?.investigatorId,
                    name: error?.name,
                    message: error?.message
                });
            }
        }
    }

    public getCallSquadMetaList(archiveId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('call', 'squadmetalist', {
                archiveId: archiveId
            }, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    public getCallSquadGalleryList(archiveId: string, offset: number, limit: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('call', 'squadgallerylist', {
                archiveId: archiveId,
                offset: offset,
                limit: limit
            }, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    public cleanSelectedCase(): void {
        // Safety net if a caller forgot disconnectAllCameras() before cleanup.
        if (this.livekitRoomMap.size > 0 || this.cameraRooms.length > 0 || this.cameraTracksMap.size > 0) {
            this.disconnectAllCameras();
        }

        // Case identity + large payloads held by BehaviorSubjects (survive navigation otherwise).
        this.profile.next(undefined);
        this.caseMetadata.next(undefined);
        this.noteMetadata.next(undefined);
        this.userMetadata.next(undefined);
        this.cameraMetadata.next(undefined);
        this.threatMetadata.next(undefined);
        this.userMapMetadata.next(undefined);
        this.investigatorGroupMetadata.next(undefined);
        this.threatDetected.next(undefined);
        this.unresolvedThreats.next([]);
        this.squadMetaList.next(undefined);
        this.squadGalleryList.next(undefined);
        this.goToLocation.next(undefined);
        this.caseUpdated.next(undefined);
        this.reloadCameras.next(undefined);
        this.reloadThreats.next(false);
        this.highlightThreatCamera.next('');
        this.antennaUpdated.next(false);
        this.routeUpdated.next(false);

        this.currentUserGroupsId = [];
        this.cameras = [];
        this.cameraGroups = [];
        this.investigatorGroups = [];
        this.buildings = [];
        this.investigators = [];
        this.squads = [];
        this.teammembers = [];
        this.admins = [];
        this.commanders = [];
        this.investigatorsAll = [];
        this.investigatorsGroup = [];
        this.investigatorsMap = [];
        this.timeline = [];
        this.currentUsermap = {};
        this.tracks = [];
        this.pinnedCams = [];
        this.caseLoaded.next(false);
        this.investigatorsMapUpdated.next(false);
        this.investigatorsUpdated.next(false);
        this.investigatorsGroupsUpdated.next(false);
        this.camerasUpdated.next(false);
        this.cameraGroupsUpdated.next(false);
        this.buildingsUpdated.next(false);
        this.timelineUpdated.next(false);
        this.documentsUpdated.next(false);
        this.markersUpdated.next(false);
        this.timelineCollapsed.next(false);
        this.camsJoined = [];
        this.cameraStreamChanged.next(undefined);
        this.cameraSessionInvestigators = [];
        this.livekitRoomMap.clear();
        this.cameraTracksMap.clear();
        this.cameraAudioMutedMap.clear();
        this.cameraRooms = [];
    }

    public combineCaseUsers(original: any[], added: any[]) {
        return this.sortChatUsers([...original, ...added]
            // Remove duplicates based on _id
            .filter((user, index, self) =>
                index === self.findIndex(u => u._id === user._id)
            ));
    }

    public sortChatUsers(users: any) {
        const rolePriority: { [key: string]: number } = {
            'Group': 1,
            'Investigator': 2,
            'Admin': 3,
            'Commander': 4
        };

        return [...users].sort((a, b) => {
            const pinDiff = Number(!!b.isPinned) - Number(!!a.isPinned);
            if (pinDiff !== 0) return pinDiff;

            const priorityDiff = Number(!!b.priority) - Number(!!a.priority);
            if (priorityDiff !== 0) return priorityDiff;

            // 1. unread true first
            if (a.unread !== b.unread) return a.unread ? -1 : 1;

            // 2. Sort by timestamp (newest first) for BOTH unread and read
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            if (aTime !== bTime) return bTime - aTime;

            // 3. Role priority
            const aRole = rolePriority[a.role] || 99;
            const bRole = rolePriority[b.role] || 99;
            if (aRole !== bRole) return aRole - bRole;

            // 4. isGroup true first
            if (a.isGroup !== b.isGroup) return a.isGroup ? -1 : 1;

            // 5. Alphabetical by name
            const aName = (a.alternative_name || a.name || '').toLowerCase();
            const bName = (b.alternative_name || b.name || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    }

    public getArchives(sessionId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('call', 'archive/list', {
                offset: 0,
                count: 10000,
                sessionId: sessionId
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    public getArchivesByDB(sessionId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('call', 'archivebydblist', {
                sessionId: sessionId
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    public getSignedUrl(archiveId: string, type: string, subType: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest(type, subType, {
                archiveId: archiveId
            }, (data, error) => {
                if (data) {
                    resolve(data);
                }
                else if (error) {
                    reject(error);
                }
            });
        });
    }

    public getTranscript(url: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.http.get(url).subscribe(data => {
                resolve(data);
            });
        });
    }

    public getCasesByCamera(cameraId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let andFilter: any = [{ "organizationId._id": { "$in": [this.requestService.orgId] } }];
            andFilter.push({ "cameras.name": { "$in": [cameraId] } });

            this.requestService.postRequest('case', 'search', {
                order: [{ "field": "name", "order": "asc" }],
                fields: ["case_id", "name"],
                term: '',
                filter: {
                    "$and": andFilter
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                }
            });
        });
    }

    public toggleRecordingBroadcast(id: string, doRecord: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            this.loadService.display(true);
            this.requestService.postRequest('camera', 'broadcast/recording', {
                id: id,
                recordingStatus: doRecord,
                recordType: 'mp4'
            }, (data, error) => {
                if (data?.results?.success) {
                    resolve(data.results.success);
                }
                else {
                    reject();
                }
                if (error) {
                    this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + error, this.translate.instant('Dismiss'));
                    reject(error);
                }
                this.loadService.display(false);
            });
        });
    }

    public getCallLogs(caseId: string, pageIndex: number, pageSize: number, selectedUser?: string, selectedGroups?: any): Promise<any> {
        let andFilter: any = [
            { "organizationId._id": { "$in": [this.requestService.orgId] } }
        ];

        if (caseId) {
            andFilter.push({ "case._id": { "$eq": caseId } });
        }

        let orFilter = [];
        if (selectedUser) {
            orFilter.push({ "investigators._id": { "$in": [selectedUser] } });
            orFilter.push({ "initiatedFrom._id": { "$eq": selectedUser } });
        }

        if (selectedGroups?.length) {
            orFilter.push({ "investigatorgroup._id": { "$in": selectedGroups } });
        }
        if (orFilter.length) {
            andFilter.push({ "$or": orFilter });
        }
        let filter = {
            "$and": andFilter,
        }

        return new Promise((resolve, reject) => {
            this.loadService.display(true);
            this.requestService.postRequest('call', 'search', {
                page: pageIndex + 1,
                count: pageSize,
                order: [{ "field": "updatedAt", "order": "desc" }],
                fields: ["createdAt", "createdBy", "type", "updatedAt", "investigators", "investigatorgroup", "initiatedFrom", "case"],
                term: "",
                filter: filter,
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data);
                }
            });
        });
    }

    public getSquadMetaList(selectedRegion: string = undefined, selectedPrefecture: string = undefined): Promise<any> {
        return new Promise((resolve, reject) => {
            let filterConfiguration: any = {
                "$and": [
                    { "organizationId._id": { "$in": [this.requestService.orgId] } }
                ]
            };
            if (selectedRegion) {
                filterConfiguration['$and'].push({ "region": { "$eq": selectedRegion } });
            }
            if (selectedPrefecture) {
                filterConfiguration['$and'].push({ "prefecture": { "$eq": selectedPrefecture } });
            }
            this.requestService.getDataList('squad', {
                page: 1,
                orderDir: 'asc',
                orderBy: 'name',
                term: '',
                perpage: 500,
                filter: filterConfiguration,
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data && data.results) {
                    const squads = data.results.filter(squad => squad.imageUrl && squad.imageUrl !== '').map(squad => {
                        return {
                            _id: squad._id,
                            imageLink: squad.imageUrl,
                        };
                    });
                    this.squadMetaList.next(squads);
                    resolve(squads);
                } else {
                    this.squadMetaList.next([]);
                    resolve([]);
                }
            });
        });
    }

    public getSquadGalleryList(selectedRegion: string = undefined, selectedPrefecture: string = undefined): Promise<any> {
        return new Promise((resolve, reject) => {
            let filterConfiguration: any = {
                "$and": [
                    { "organizationId._id": { "$in": [this.requestService.orgId] } },
                    { "type": { "$eq": 'squad-art' } }
                ]
            };
            if (selectedRegion) {
                filterConfiguration['$and'].push({ "region": { "$eq": selectedRegion } });
            }
            if (selectedPrefecture) {
                filterConfiguration['$and'].push({ "prefecture": { "$eq": selectedPrefecture } });
            }
            this.requestService.getDataListByList('img', {
                page: 1,
                orderDir: 'asc',
                orderBy: 'name',
                term: '',
                perpage: 99999999,
                filter: filterConfiguration,
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data && data.results) {
                    const squads = data.results.filter(squad => squad.imageUrl && squad.imageUrl !== '').map(squad => {
                        return {
                            _id: squad._id,
                            imageLink: squad.imageUrl,
                        };
                    });
                    this.squadGalleryList.next(squads);
                    resolve(squads);
                } else {
                    this.squadGalleryList.next([]);
                    resolve([]);
                }
            });
        });
    }

    public mapSquadData(squads: any) {
        let squadsData = squads.filter((squad: any) => squad !== undefined).map((squad: any) => {
            const markerId = squad._id;
            let actualSquadId = squad.linkId;
            const squadGalleryList = this.squadGalleryList.getValue() || [];

            const squadMetaData = squadGalleryList.find((squadMetaData: any) => squadMetaData._id === actualSquadId);
            if (squadMetaData) {
                let geolocation: any = {};
                try {
                    geolocation = JSON.parse(squad.geolocation);
                    if (geolocation.lat && geolocation.lng) {
                        squad.lat = geolocation.lat;
                        squad.lng = geolocation.lng;
                    }
                } catch (error) { }
                return {
                    _id: markerId,
                    name: squad.name,
                    description: squad.description,
                    lat: squad.lat,
                    lng: squad.lng,
                    geolocation: squad.geolocation,
                    icon: squadMetaData.imageLink,
                    markerType: 'squad',
                    squad: squadMetaData,
                    draggable: true,
                    showLabel: true,
                    label: squad.name,
                    createdAt: new Date().toISOString()
                };
            }
            return undefined;
        });
        return squadsData.filter((squad: any) => squad !== undefined);
    }

    public mapTeammemberData(teammembers: any, investigators: any[] = []) {
        let teammembersData = teammembers.filter((teammember: any) => teammember !== undefined).map((teammember: any) => {
            const markerId = teammember._id;
            const investigatorData = investigators.find((inv: any) => inv._id === teammember.linkId);
            if (investigatorData) {
                let geolocation: any = {};
                try {
                    geolocation = JSON.parse(teammember.geolocation);
                    if (geolocation.lat && geolocation.lng) {
                        teammember.lat = geolocation.lat;
                        teammember.lng = geolocation.lng;
                    }
                } catch (error) { }
                return {
                    _id: markerId,
                    name: investigatorData.alternative_name || investigatorData.name,
                    lat: teammember.lat,
                    lng: teammember.lng,
                    geolocation: teammember.geolocation,
                    icon: investigatorData.pictureLink,
                    markerType: 'teammember',
                    investigator: investigatorData,
                    pictureLink: investigatorData.pictureLink,
                    alternative_name: investigatorData.alternative_name || investigatorData.name,
                    createdAt: new Date().toISOString(),
                    draggable: true,
                    showLabel: true,
                    label: investigatorData.alternative_name || investigatorData.name,
                    labelColor: investigatorData.isGroup ? '#255290' : '#7030A0',
                    isGroup: investigatorData.isGroup || false,
                    picture_icon: investigatorData.picture_icon || undefined,
                    squad_ref: investigatorData.squad_ref || undefined,
                    isInCall: investigatorData.isInCall || false,
                    inLiveStreaming: investigatorData.inLiveStreaming || false,
                };
            }
            return undefined;
        });
        return teammembersData.filter((teammember: any) => teammember !== undefined);
    }

    public deleteTeammemberByLinkIdMarker(linkId: string, caseId: string) {
        let markerId = this.teammembers.find((teammember: any) => teammember.linkId === linkId)?._id;
        this.deleteMarker(markerId, caseId);
    }

    public deleteMarker(_id: string, caseId: string) {
        this.loadService.display(true);
        this.requestService.postRequest('markers', _id + '/delete', {}, (data, error) => {
            if (data) {
                this.signalingService.publish(`case-${caseId}`, {
                    type: 'markers-updated',
                    caseId: caseId,
                    markerId: _id,
                    action: 'delete'
                }, this.signalingService.livePinsTopicName);
                // this.layoutUtilsService.showNotification(this.translate.instant('Deleted successfully'), this.translate.instant('Dismiss'));
            }
            if (error) {
                this.layoutUtilsService.showNotification(this.translate.instant('An error has occurred. Please try again later.'), this.translate.instant('Dismiss'));
            }
            this.loadService.display(false);
        });
    }

    public saveMarker(caseId: string, marker: any, showNotification: boolean = false): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload: any = {
                geolocation: marker.geolocation,
                linkId: marker.linkId || undefined,
                type: marker.markerType,
                name: marker.name || '',
                description: marker.description || '',
                case: { _id: caseId }
            };
            let endpointId = '';
            let successMessageKey = 'Created successfully';

            if (marker._id) {
                payload._id = marker._id;
                endpointId = marker._id;
                successMessageKey = 'Updated successfully';
            }
            if (showNotification) {
                this.loadService.display(true);
            }
            this.requestService.postRequest('markers', endpointId, payload, (data, error) => {
                if (data) {
                    this.signalingService.publish(`case-${caseId}`, {
                        type: 'markers-updated',
                        caseId: caseId,
                        markerId: data._id || marker._id,
                        action: marker._id ? 'update' : 'create',
                        geolocation: marker.geolocation,
                        markerType: marker.markerType
                    }, this.signalingService.livePinsTopicName);
                    if (showNotification)
                        this.layoutUtilsService.showNotification(this.translate.instant(successMessageKey), this.translate.instant('Dismiss'));
                    resolve(data);
                }
                if (error) {
                    if (showNotification) {
                        this.layoutUtilsService.showNotification(this.translate.instant('An error has occurred. Please try again later.'), this.translate.instant('Dismiss'));
                    }
                    reject(error);
                }
                if (showNotification) {
                    this.loadService.display(false);
                }
            });
        });
    }

    addBaseStation(caseId: string, baseStation: any): Promise<any> {
        return new Promise((resolve, reject) => {
            const body: any = {
                case: {
                    _id: caseId,
                },
                title: baseStation.title,
                description: baseStation.description,
                color: baseStation.color,
                shape: baseStation.shape,
                rotation_start_at: baseStation.rotationStartAt,
                rotation_end_at: baseStation.rotationEndAt,
                shape_length: baseStation.length,
                geolocation: JSON.stringify(baseStation.baseLocation),
                address: baseStation.address
            };
            if (baseStation.date && baseStation.time) {
                body.datetime = moment(baseStation.date + ' ' + baseStation.time, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD h:mm A']).toISOString();
            } else {
                body.datetime = '';
            }
            this.requestService.postRequest('antennabasedlocation', '', body, (data, error) => {
                if (data) {
                    this.antennaUpdated.next(true);
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    updateBaseStation(baseStation: any, baseStationId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const body: any = {
                title: baseStation.title,
                description: baseStation.description,
                color: baseStation.color,
                shape: baseStation.shape,
                rotation_start_at: baseStation.rotationStartAt,
                rotation_end_at: baseStation.rotationEndAt,
                shape_length: baseStation.length,
                geolocation: JSON.stringify(baseStation.baseLocation),
                address: baseStation.address
            };
            if (baseStation.date && baseStation.time) {
                body.datetime = moment(baseStation.date + ' ' + baseStation.time, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD h:mm A']).toISOString();
            } else {
                body.datetime = '';
            }
            this.requestService.postRequest('antennabasedlocation', baseStationId, body, (data, error) => {
                if (data) {
                    this.antennaUpdated.next(true);
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    listAntennaBasedLocations(caseId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('antennabasedlocation', 'case/' + caseId, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    deleteAntennaBasedLocation(baseStationId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('antennabasedlocation', baseStationId + '/delete', undefined, (data, error) => {
                if (data) {
                    this.antennaUpdated.next(true);
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    getCameraTracks(investigatorId: string): any[] {
        const tracks = this.cameraTracksMap.get(investigatorId) || [];
        return [...tracks];
    }

    getCameraAudioMuted(cameraId: string): boolean {
        if (!cameraId) {
            return true;
        }
        return this.cameraAudioMutedMap.has(cameraId)
            ? !!this.cameraAudioMutedMap.get(cameraId)
            : true;
    }

    setCameraAudioMuted(cameraId: string, muted: boolean): void {
        if (!cameraId) {
            return;
        }
        this.cameraAudioMutedMap.set(cameraId, muted);
    }

    getThreats(caseId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('threatsmeta', 'list', { cases: [{ _id: caseId }] }, (data, error) => {
                if (data) {
                    data.results.map((threat: any) => {
                        threat.dispatchedBy = threat.dispatchedBy?.name;
                    });
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    getThreatMetadata(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.getMetadataPromise('threatsmeta').then((metadata: any) => {
                this.threatMetadata.next(metadata.results);
                resolve(metadata.results);
            }).catch((e) => reject(e));
        });
    }

    getDetectedThreatById(threatId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('threatsmeta', threatId, (data, error) => {
                if (data) {
                    resolve(data.results || data);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    deleteDetectedThreat(threatId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('threatsmeta', threatId + '/delete', undefined, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    getDetectionFeedback(threatId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord(threatId + '/feedback', 'detections', (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });

            // this.http.get(`${baseUrl}/detections/${encodeURIComponent(threatId)}/feedback`).subscribe({
            //     next: (data: any) => resolve(data?.sample || null),
            //     error: (error) => reject(error)
            // });
        });
    }

    submitDetectionFeedback(
        threatId: string,
        status: 'correct' | 'incorrect' | 'uncertain',
        options?: {
            reviewedBy?: string;
            note?: string;
        }
    ): Promise<any> {
        const payload: any = { status };
        const reviewedBy = options?.reviewedBy;
        if (reviewedBy) {
            payload.reviewedBy = reviewedBy;
        }
        const note = String(options?.note || "").trim();
        if (note) {
            payload.note = note;
        }
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('detections', threatId + '/feedback', payload, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
            // this.http.post(`${baseUrl}/detections/${encodeURIComponent(threatId)}/feedback`, payload).subscribe({
            //     next: (data: any) => resolve(data?.sample || data),
            //     error: (error) => reject(error)
            // });
        });
    }

    /** Bulk-delete `threats_meta` documents (slideshow / frame rows). POST `threatsmeta/meta/delete`. */
    deleteThreatsMetaByIds(threatsMetaIds: string[]) {
        return new Promise((resolve, reject) => {
            if (!threatsMetaIds?.length) {
                resolve(undefined);
                return;
            }
            this.requestService.postRequest(
                'threatsmeta',
                'meta/delete',
                { threats_meta_ids: threatsMetaIds },
                (data, error) => {
                    if (data) {
                        resolve(data.results);
                    }
                    if (error) {
                        reject(error);
                    }
                }
            );
        });
    }

    getCamera(cameraId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('camera', cameraId, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    getSortedInvestigatorsByDistance(caseId, geolocation): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('case', `investigators/${caseId}/bylocation`, {
                geolocation
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                }
            });
        });
    }

    getCasesForCamera(cameraId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('case', `all/${cameraId}`, (data, error) => {
                if (data) {
                    resolve(data.results);
                }
                if (error) {
                    reject(error);
                }
            });
        });
    }

    getAllCameras(type: string = 'building-cams'): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('camera', 'search', {
                page: 1,
                count: 1000,
                order: [{ "field": "name", "order": "asc" }],
                fields: ["_id", "camera_id", "name", "description", "stream_type", "stream_url", "camera_type", "camera_sub_type"],
                term: '',
                filter: {
                    "$or": [{ "buildingId": { $exists: false } }, { "buildingId._id": "" }, { "buildingId._id": null }],
                    "organizationId._id": { "$in": [this.requestService.orgId] },
                    "camera_type": { "$eq": type },
                    "prefecture": { "$eq": this.requestService.currentUser.prefecture },
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                };
            });
        });
    }
    getCamerasByIds(ids: string[]): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('camera', 'search', {
                page: 1,
                count: 1000,
                order: [{ "field": "name", "order": "asc" }],
                fields: ["_id", "camera_id", "name", "description", "stream_type", "stream_url", "camera_type", "camera_sub_type"],
                term: '',
                filter: {
                    "organizationId._id": { "$in": [this.requestService.orgId] },
                    "_id": { "$in": ids },
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results);
                };
            });
        });
    }
    getTabletCamerasByIds(ids: string[]): Promise<any[]> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('camera', 'search', {
                page: 1,
                count: 1000,
                order: [{ "field": "name", "order": "asc" }],
                fields: ["_id", "camera_id", "name", "camera_type", "buildingId", "floor", "roomName", "tabletId"],
                term: '',
                filter: {
                    "organizationId._id": { "$in": [this.requestService.orgId] },
                    "_id": { "$in": ids },
                    "camera_type": { "$eq": "tablet-cam" },
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    resolve(data.results || []);
                };
            });
        });
    }
    public triggerBuildingsCaseUpdate(caseId: string): Promise<void> {
        const camerasPromise = this.getBuildings(caseId);

        return Promise.all([camerasPromise])
            .then(() => {
                this.buildingsUpdated.next(true);
            })
            .catch(() => {
                // swallow errors to avoid breaking caller flows
            })
            .then(() => { });
    }

    getBuildings(caseId): Promise<void> {
        return new Promise((resolve, reject) => {
            this.requestService.getRecord('', `case/building/${caseId}/list`, (data, error) => {
                if (error) {
                    reject(error);
                }
                if (data) {
                    this.buildings = data.results;
                    resolve(data);
                }
            });
        });
    }

    getUnResolvedThreats(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('threatsmeta', 'building/unsolved', {}, (data, error) => {
                if (data) {
                    this.unresolvedThreats.next(data.results);
                    resolve(data.results);
                }
                resolve([]);
                // if (error) {
                //     reject(error);
                // }
            });
        });
    }

    openSaveRouteDialog(caseId: string,
        polylinePoints: Array<{ _id?: string; index?: number; lat: number; lng: number; checkpointAt?: string; checkpointOffsetMinutes?: number; toleranceTimerMinutes?: number; isStartPoint?: boolean; isEndPoint?: boolean }>,
        width: string = '420px',
        options?: OpenSaveRouteDialogOptions
    ): Promise<SaveSafeRouteDialogResult | undefined> {
        return new Promise(async (resolve) => {
            let cameras: any = await this.getCamerasWithNoRoute(caseId);
            const settings = this.requestService.getSettings(this.requestService.pageOrganization.getValue());
            const dialogRef = this.dialog.open(SaveSafeRouteDialogComponent, {
                data: {
                    cameras,
                    polylinePoints,
                    selectedCameraIds: options?.selectedCameraIds || [],
                    selectedColor: options?.selectedColor,
                    selectedName: options?.selectedName,
                    selectedDeviationDistanceMeters: options?.selectedDeviationDistanceMeters,
                    distanceUnit: settings.distanceUnit,
                    isUpdate: !!options?.isUpdate,
                    currentRouteId: options?.currentRouteId || null
                },
                width,
                autoFocus: false
            });
            dialogRef.afterClosed().subscribe((result) => resolve(result));
        });
    }

    createSafeRoute(
        caseId: string,
        coordinates: Array<{ _id?: string; index?: number; lat: number; lng: number; checkpointAt?: string; checkpointOffsetMinutes?: number; toleranceTimerMinutes?: number; isStartPoint?: boolean; isEndPoint?: boolean } | { _id?: string; index?: number; lng: number; lat: number; checkpointAt?: string; checkpointOffsetMinutes?: number; toleranceTimerMinutes?: number; isStartPoint?: boolean; isEndPoint?: boolean } | [number, number]>,
        cameras: SaveSafeRouteCameraRef[],
        color: string,
        name: string,
        routeId?: string,
        deviationDistanceMeters?: number
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            const normalizedCoordinates: SafeRouteCoordinatePayload[] = (coordinates || [])
                .map((point: any) => {
                    if (Array.isArray(point) && point.length >= 2) {
                        const lng = Number(point[0]);
                        const lat = Number(point[1]);
                        if (Number.isFinite(lng) && Number.isFinite(lat)) {
                            return { _id: uuidv4(), lng, lat };
                        }
                        return null;
                    }
                    const lng = Number(point?.lng ?? point?.lon);
                    const lat = Number(point?.lat);
                    if (Number.isFinite(lng) && Number.isFinite(lat)) {
                        const normalizedPoint: SafeRouteCoordinatePayload = { lng, lat };
                        normalizedPoint._id = String(point?._id || '').trim() || uuidv4();
                        const checkpointAt = String(point?.checkpointAt || '').trim();
                        if (checkpointAt) {
                            normalizedPoint.checkpointAt = checkpointAt;
                        }
                        const checkpointOffsetMinutes = Number(point?.checkpointOffsetMinutes);
                        if (Number.isFinite(checkpointOffsetMinutes) && checkpointOffsetMinutes >= 0) {
                            normalizedPoint.checkpointOffsetMinutes = Math.round(checkpointOffsetMinutes);
                        }
                        const toleranceTimerMinutes = Number(point?.toleranceTimerMinutes);
                        if (Number.isFinite(toleranceTimerMinutes) && toleranceTimerMinutes >= 0) {
                            normalizedPoint.toleranceTimerMinutes = Math.round(toleranceTimerMinutes);
                        }
                        return normalizedPoint;
                    }
                    return null;
                })
                .filter((point: SafeRouteCoordinatePayload | null): point is SafeRouteCoordinatePayload => !!point);
            const lastCoordinateIndex = normalizedCoordinates.length - 1;
            normalizedCoordinates.forEach((point, index) => {
                point.index = index;
                point.isStartPoint = index === 0;
                point.isEndPoint = index > 0 && index === lastCoordinateIndex;
                if (index === 0) {
                    delete point.checkpointOffsetMinutes;
                    delete point.toleranceTimerMinutes;
                }
            });
            const payload = {
                caseId: { _id: caseId },
                coordinates: normalizedCoordinates,
                cameras,
                color: String(color || '#4CAF50'),
                name: String(name || '').trim(),
                ...(Number.isFinite(Number(deviationDistanceMeters)) ? { deviationDistanceMeters: Number(deviationDistanceMeters) } : {})
            };
            this.requestService.postRequest('saferoute', routeId || '', payload, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data);
            });
        });
    }

    listSafeRoutes(caseId: string): Promise<any[]> {
        let andFilter: any = [
            { "organizationId._id": { "$in": [this.requestService.orgId] } },
            { "caseId._id": { "$eq": caseId } },
        ];

        return new Promise((resolve, reject) => {
            this.requestService.postRequest('saferoute', `search`, {
                page: 1,
                count: 10000,
                order: [{ "field": "createdAt", "order": "desc" }],
                fields: [],
                termfieldstermfields: [''],
                term: '',
                filter: {
                    "$and": andFilter
                }
            }, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data?.results?.results || []);
            });
        });
    }

    deleteSafeRoute(routeId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('saferoute', `${routeId}/delete`, {}, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data);
            });
        });
    }

    getCamerasWithNoRoute(caseId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('camera/list', `notsaferoute/${caseId}`, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data?.results || []);
            });
        });
    }

    getPinnedCameras(userId: string, caseId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.getRequest('camera', `pinned/get/${userId}/${caseId}`, (data, error) => {
                if (error) {
                    this.pinnedCams = [];
                    reject(error);
                    return;
                }
                this.pinnedCams = data?.results || [];
                resolve(data?.results || []);
            });
        });
    }

    pinCam(userId: string, cameraId: string, caseId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('camera', 'pinned/create', {
                caseId: caseId,
                userId: userId,
                cameraId: cameraId,
            }, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data?.results || []);
            }
            )
        });
    }

    unpinCam(userId: string, cameraId: string, caseId: string) {
        return new Promise((resolve, reject) => {
            this.requestService.postRequest('camera', 'pinned/delete', {
                caseId: caseId,
                userId: userId,
                cameraId: cameraId,
            }, (data, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(data?.results || []);
            }
            )
        });
    }
}
